import { useState } from 'react'
import { PageContainer, parseValueToDay, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { Alert, App, Button, Tag, type TableProps } from 'antd'
import dayjs from 'dayjs'
import { Plus, Trash2 } from 'lucide-react'
import type { OrderGroup, OrderGroupListItem } from '@super-mega/server/contracts/order'
import { TableRowActions, type TableRowAction } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, TABLE_ACTION_COLUMN } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  BillActionDialog,
  createOrderGroupBillOperation,
  refreshMoneyQueries,
  type BillOperation
} from '@/features/bill/index.ts'
import {
  DEFAULT_ORDER_GROUP_LIST_SEARCH,
  OrderGroupEditorModal,
  OrderGroupIncludeModal,
  orderGroupListSearchSchema,
  type OrderGroupListSearch
} from '@/features/order/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useRemoteKeyword } from '@/hooks/use-remote-keyword.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import { mergeOptions } from '@/utils/select-options.ts'

type CalendarValue = {
  startOf: (unit: 'day') => CalendarValue
  endOf: (unit: 'day') => CalendarValue
  toDate: () => Date
}

type CalendarInput = CalendarValue | string | null | undefined

type OrderGroupSearchFormValues = {
  keyword?: string
  customerId?: number[]
  creatorKeyword?: string
  createdAtRange?: [CalendarInput, CalendarInput]
}

function toCalendarValue(value: CalendarInput): CalendarValue | undefined {
  if (!value) {
    return undefined
  }
  if (typeof value !== 'string') {
    return value
  }
  const parsed = parseValueToDay(value)
  return parsed && !Array.isArray(parsed) ? parsed : undefined
}

const listQuery = createListQueryPersistence('super-mega:orders:groups:list-query', orderGroupListSearchSchema)

export const Route = createFileRoute('/_authenticated/orders/groups/')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: OrderGroupsPage,
  head: () => ({ meta: [{ title: '订单组 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function OrderGroupsPage() {
  const trpc = useTRPC()
  const access = useAccess()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const batchDeleteGroups = useMutation(trpc.order.groups.batchDelete.mutationOptions())
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
  const deleteGroup = useMutation(trpc.order.groups.delete.mutationOptions())
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [editing, setEditing] = useState<OrderGroup | 'new'>()
  const [including, setIncluding] = useState<OrderGroup>()
  const [billOperation, setBillOperation] = useState<BillOperation>()
  const canManageBills =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('bill.') && !permission.endsWith('.read'))
  const canCreate = currentSession.isSuperAdmin || currentSession.permissions.includes('order.rental.create')
  const canUpdate =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('order.group.') && !permission.endsWith('.read')
    )
  const groups = useQuery({
    ...trpc.order.groups.list.queryOptions(search),
    placeholderData: keepPreviousData
  })
  const customerSearch = useRemoteKeyword()
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({ purpose: 'order.group.read', keyword: customerSearch.keyword }),
    staleTime: 60_000
  })
  const customerOptions = mergeOptions(
    (customerSearch.keyword ? [] : (groups.data?.data ?? [])).map(({ customerId, customerName }) => ({
      label: customerName,
      value: customerId
    })),
    customers.data ?? []
  )
  const invalidateGroups = async () => {
    await Promise.all([
      queryClient.invalidateQueries(trpc.order.groups.list.queryFilter()),
      queryClient.invalidateQueries(trpc.order.groups.options.queryFilter()),
      queryClient.invalidateQueries(trpc.order.groups.overview.queryFilter())
    ])
  }
  const table = usePageTableNavigation<OrderGroupListSearch, OrderGroupSearchFormValues>({
    defaultSearch: DEFAULT_ORDER_GROUP_LIST_SEARCH,
    getFormInitialValues: (currentSearch) => {
      const createdFrom = toCalendarValue(currentSearch.createdFrom)
      const createdTo = toCalendarValue(currentSearch.createdTo)
      return {
        keyword: currentSearch.keyword ?? (currentSearch.id === undefined ? undefined : String(currentSearch.id)),
        customerId: currentSearch.customerId,
        creatorKeyword: currentSearch.creatorKeyword,
        createdAtRange: createdFrom && createdTo ? [createdFrom, createdTo] : undefined
      }
    },
    getSearchFromForm: (values) => {
      const createdFrom = toCalendarValue(values.createdAtRange?.[0])
      const createdTo = toCalendarValue(values.createdAtRange?.[1])
      return {
        id: undefined,
        keyword: values.keyword?.trim() || undefined,
        customerId: values.customerId?.length ? values.customerId : undefined,
        customerKeyword: undefined,
        creatorKeyword: values.creatorKeyword?.trim() || undefined,
        createdFrom: createdFrom ? dayjs(createdFrom.startOf('day').toDate()).toISOString() : undefined,
        createdTo: createdTo ? dayjs(createdTo.endOf('day').toDate()).toISOString() : undefined
      }
    },
    navigate: navigateSearch,
    onQueryChange: () => {
      setEditing(undefined)
      setSelectedGroupIds([])
    },
    search,
    showTotal: (total) => `共 ${total} 个订单组`
  })
  const handleTableChange: NonNullable<TableProps<OrderGroupListItem>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      setSelectedGroupIds([])
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = currentSorter.order === 'ascend' ? 'asc' : 'desc'
      setEditing(undefined)
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const confirmDeleteGroups = (ids: number[], groupName?: string) => {
    const targetIds = [...ids]
    const targetIdSet = new Set(targetIds)
    modal.confirm({
      title: groupName ? `删除订单组 ${groupName}（ID：${targetIds[0]}）？` : `删除已选 ${targetIds.length} 个订单组？`,
      content: '订单组删除后无法恢复。关联的租赁订单和服务订单会保留，订单组 ID 将清空，客户、办理状态和账单不受影响。',
      focusable: { autoFocusButton: 'cancel' },
      okText: groupName ? '删除订单组' : '确认批量删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (groupName) {
            await deleteGroup.mutateAsync({ orderGroupId: targetIds[0] })
          } else {
            await batchDeleteGroups.mutateAsync({ ids: targetIds })
          }
        } catch (error) {
          void message.error(getErrorMessage(error, '删除订单组失败'))
          throw error
        }
        setEditing(undefined)
        setSelectedGroupIds([])
        void message.success(`已删除 ${targetIds.length} 个订单组，关联订单已移出分组`)
        await Promise.all([
          invalidateGroups(),
          queryClient.invalidateQueries(trpc.order.groups.detail.queryFilter()),
          queryClient.invalidateQueries(trpc.order.rental.list.queryFilter()),
          queryClient.invalidateQueries(trpc.order.rental.detail.queryFilter()),
          queryClient.invalidateQueries(trpc.order.service.list.queryFilter()),
          queryClient.invalidateQueries(trpc.order.service.detail.queryFilter())
        ])
        if (groups.data && groups.data.data.every((group) => targetIdSet.has(group.id)) && search.current > 1) {
          await navigateSearch({
            replace: true,
            search: (previous) => ({ ...previous, current: previous.current - 1 })
          })
        }
      }
    })
  }

  const columns: ProColumns<OrderGroupListItem>[] = [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '订单组名称或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'customerId',
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        mode: 'multiple',
        options: customerOptions,
        loading: customers.isFetching,
        ...proRemoteSearch(customerSearch.onSearch)
      },
      hideInTable: true,
      title: '客户'
    },
    {
      dataIndex: 'creatorKeyword',
      fieldProps: { allowClear: true, placeholder: '创建人姓名或邮箱' },
      hideInTable: true,
      title: '创建人'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      copyable: true,
      dataIndex: 'id',
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      ellipsis: true,
      render: (_, group) => (
        <Link
          className="text-content! hover:text-accent! block max-w-full truncate font-semibold underline! underline-offset-2 transition-colors"
          title="在新标签页查看订单组"
          to="/orders/groups/$groupId"
          params={{ groupId: String(group.id) }}
          search={{ kind: 'rental' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {group.name}
        </Link>
      ),
      search: false,
      title: '订单组名称',
      width: '18rem'
    },
    {
      dataIndex: 'customerName',
      ellipsis: true,
      render: (_, group) => {
        if (!group.customerName) return '—'
        if (!access.visit(`/customer-management/customers/${group.customerId}/documents`) || !access.can('bill.read')) {
          return group.customerName
        }
        return (
          <Link
            className="text-content! hover:text-accent! underline! underline-offset-2 transition-colors"
            title="在新标签页查看客户账单"
            to="/customer-management/customers/$customerId/documents"
            params={{ customerId: String(group.customerId) }}
            search={{ kind: 'bills' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {group.customerName}
          </Link>
        )
      },
      search: false,
      title: '客户',
      width: '18rem'
    },
    {
      dataIndex: 'orderCount',
      render: (_, group) => (
        <div className="flex flex-wrap gap-1">
          <Tag>{group.orderCount} 个订单</Tag>
          {group.serviceOrderCount > 0 ? <Tag color="blue">服务 {group.serviceOrderCount}</Tag> : null}
          {group.rentalOrderCount > 0 ? <Tag color="green">租赁 {group.rentalOrderCount}</Tag> : null}
        </div>
      ),
      search: false,
      title: '订单数量',
      width: '9rem'
    },
    {
      dataIndex: 'creator',
      render: (_, group) => <UserAvatarName avatar={group.creator.avatar} name={group.creator.name} size={28} />,
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: search.sortOrder === 'asc' ? 'ascend' : 'descend',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      ...TABLE_ACTION_COLUMN,
      render: (_, group) => (
        <OrderGroupActions
          group={group}
          canUpdate={canUpdate}
          canManageBills={canManageBills}
          onInclude={setIncluding}
          onEdit={setEditing}
          onDelete={() => confirmDeleteGroups([group.id], group.name)}
          onBillOperation={setBillOperation}
        />
      )
    }
  ]

  return (
    <PageContainer
      breadcrumbRender={false}
      content="将同一客户的租赁订单和服务订单归入一组，分别办理、分别出账。"
      title="订单组"
    >
      {groups.isError ? (
        <Alert
          action={
            <Button onClick={() => void groups.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(groups.error, '订单组列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<OrderGroupListItem, OrderGroupListSearch>
        columns={columns}
        columnsState={{ persistenceKey: 'orders-groups:table', persistenceType: 'localStorage' }}
        dataSource={groups.data?.data ?? []}
        headerTitle={`订单组（${groups.data?.total ?? 0}）`}
        loading={{ delay: 150, spinning: groups.isFetching }}
        onChange={handleTableChange}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void groups.refetch() }}
        pagination={{ ...table.pagination, total: groups.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canUpdate
            ? {
                selectedRowKeys: selectedGroupIds,
                preserveSelectedRowKeys: false,
                onChange: (keys) => setSelectedGroupIds(keys.map(Number)),
                getCheckboxProps: () => ({
                  disabled: groups.isFetching || batchDeleteGroups.isPending || deleteGroup.isPending
                })
              }
            : undefined
        }
        scroll={{ x: '92rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() => [
          ...(canUpdate
            ? [
                <Button
                  key="batch-delete"
                  danger
                  icon={<Trash2 aria-hidden size={16} />}
                  disabled={selectedGroupIds.length === 0 || groups.isFetching || deleteGroup.isPending}
                  loading={batchDeleteGroups.isPending}
                  onClick={() => confirmDeleteGroups(selectedGroupIds)}
                >
                  {selectedGroupIds.length ? `批量删除（${selectedGroupIds.length}）` : '批量删除'}
                </Button>
              ]
            : []),
          ...(canCreate
            ? [
                <Authorized key="new" permissions={['order.group.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => setEditing('new')}
                    type="primary"
                  >
                    新建订单组
                  </Button>
                </Authorized>
              ]
            : [])
        ]}
      />
      {including && (
        <OrderGroupIncludeModal key={including.id} group={including} onClose={() => setIncluding(undefined)} />
      )}
      <OrderGroupEditorModal
        editing={editing}
        onClose={() => setEditing(undefined)}
        onSaved={async () => {
          setEditing(undefined)
          await invalidateGroups()
        }}
      />
      {billOperation && (
        <BillActionDialog
          operation={billOperation}
          onClose={() => setBillOperation(undefined)}
          onSaved={async () => {
            await Promise.all([refreshMoneyQueries(queryClient, trpc), invalidateGroups()])
          }}
        />
      )}
    </PageContainer>
  )
}

function OrderGroupActions({
  group,
  canUpdate,
  canManageBills,
  onInclude,
  onEdit,
  onDelete,
  onBillOperation
}: {
  group: OrderGroupListItem
  canUpdate: boolean
  canManageBills: boolean
  onInclude: (group: OrderGroupListItem) => void
  onEdit: (group: OrderGroupListItem) => void
  onDelete: () => void
  onBillOperation: (operation: BillOperation) => void
}) {
  const router = useRouter()
  const actions: TableRowAction[] = [
    {
      key: 'view',
      label: '查看',
      href: router.buildLocation({
        to: '/orders/groups/$groupId',
        params: { groupId: String(group.id) },
        search: { kind: 'rental' }
      }).href,
      target: '_blank',
      rel: 'noopener noreferrer'
    }
  ]
  if (canManageBills) {
    actions.push({
      key: 'issue-bills',
      label: '一键出账',
      onClick: () => onBillOperation(createOrderGroupBillOperation(group.id))
    })
  }
  if (canUpdate) {
    actions.push(
      { key: 'include', label: '纳入订单组', onClick: () => onInclude(group) },
      { key: 'edit', label: '编辑', onClick: () => onEdit(group) },
      { key: 'delete', label: '删除', danger: true, onClick: onDelete }
    )
  }
  return <TableRowActions resource="order.group" actions={actions} maxVisibleActions={1} />
}
